import React, { useEffect, useRef, useState } from "react"

import { NIRVANA_COLORS } from "@front-shared/config/theme.config.ts"
import { ButtonElement } from "@front-shared/elements/button.element.tsx"
import { PopupWrapperElement } from "@front-shared/elements/popupWrapper.element.tsx"
import { PrimaryText } from "@front-shared/elements/primaryText.element.tsx"
import type { IPopupProps } from "@front-shared/types/props.types.ts"

import {
    BulkUploadStage,
    NETWORK_STATUS_CONFIG,
} from "../../config/networkStatus.config"
import { NetworkStatusRulesCsvHelper } from "../../helpers/networkStatusRulesCsv.helper"
import { useAppDispatch } from "../../store/appDispatch.hook"
import { useAppSelector } from "../../store/selectors/app.selector"
import { selectedPracticeRoleSelector } from "../../store/selectors/selectedPracticeRole.selector"
import { networkStatusValidateRules } from "../../store/thunks/networkStatus.thunks"
import type { IReviewNewRulesLocationState } from "../../types/networkStatus.types.ts"

import trashIcon from "@front-shared/assets/images/icons/trash-dark2.svg"

export const NetworkStatusRulesBulkUploadPopupComponent: React.FunctionComponent<
    INetworkStatusRulesBulkUploadPopupComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const fileInputRef = useRef<HTMLInputElement>(null)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [stage, setStage] = useState<BulkUploadStage>(BulkUploadStage.INITIAL)
    const [selectedFile, setSelectedFile] = useState<File | null>(null)

    const onRemoveFile = () => {
        setSelectedFile(null)
        setStage(BulkUploadStage.INITIAL)
    }

    // Reset when popup closes so next open starts fresh
    useEffect(() => {
        if (props.isActive) {
            return
        }

        onRemoveFile()
    }, [props.isActive])

    const onDownloadTemplate = () => {
        window.open(
            NETWORK_STATUS_CONFIG.csvTemplateUrl,
            "_blank",
            "noopener,noreferrer",
        )
    }

    const handleFile = (file: File) => {
        const name = file.name.toLowerCase()

        if (
            !name.endsWith(".csv") &&
            file.type !== "text/csv" &&
            file.type !== "application/csv" &&
            file.type !== "text/plain"
        ) {
            return
        }

        setSelectedFile(file)
        setStage(BulkUploadStage.STAGING)
    }

    const onDragOver = (e: React.DragEvent) => {
        e.preventDefault()
        e.stopPropagation()
    }

    const onDrop = (e: React.DragEvent) => {
        onDragOver(e)

        const file = e.dataTransfer?.files?.[0]

        if (file) {
            handleFile(file)
        }
    }

    const onManualSelect = () => {
        fileInputRef.current?.click()
    }

    const onFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]

        if (file) {
            handleFile(file)
        }

        e.target.value = ""
    }

    const runBulkUpload = async () => {
        if (!selectedFile || !selectedPracticeRole) {
            return
        }

        const text = await selectedFile.text()

        const rules = NetworkStatusRulesCsvHelper.parse(text)

        if (!rules.length) {
            setStage(BulkUploadStage.PARSING_ERROR)

            return
        }

        setStage(BulkUploadStage.PARSING)

        const rulesWithIndex = rules.map((item, i) => {
            return {
                ...item,
                index: i,
            }
        })

        dispatch(
            networkStatusValidateRules({
                practice: selectedPracticeRole.practice,
                payload: {
                    rules: rulesWithIndex,
                },
                onSuccess: result => {
                    props.onValidationDone({
                        rules: rulesWithIndex,
                        errors: result?.errors ?? [],
                        filename: selectedFile.name,
                        totalRowCount: rulesWithIndex.length,
                    })

                    props.onDone()
                },
            }),
        )
    }

    const renderContent = () => {
        if (stage === BulkUploadStage.PARSING_ERROR) {
            return (
                <>
                    <PrimaryText
                        typography="subtitleSemibold"
                        className="
                            mt-[24px] flex items-center justify-between gap-[12px]
                            rounded-[8px] border border-solid border-brand-black-transparent-010
                            bg-brand-offWhite py-[16px] px-[24px]
                        "
                    >
                        No valid data rows found in CSV
                    </PrimaryText>

                    <div className="flex justify-end mt-[24px]">
                        <ButtonElement
                            label="Done"
                            type="primary"
                            htmlType="button"
                            onClick={props.onDone}
                        />
                    </div>
                </>
            )
        }

        if (stage === BulkUploadStage.PARSING) {
            return (
                <div
                    className="
                        mt-[24px] py-[16px] px-[24px] rounded-[8px] bg-brand-offWhite flex flex-col gap-[8px]
                    "
                >
                    <PrimaryText typography="subtitleSemibold">
                        Parsing your network rules...
                    </PrimaryText>

                    <PrimaryText>This can take up to 10 seconds.</PrimaryText>

                    <div className="flex flex-col gap-[12px]">
                        {[72, 88, 64, 96, 80].map((w, i) => (
                            <div
                                key={i}
                                className="h-[16px] rounded-[4px] bg-brand-warmLight animate-pulse"
                                style={{ width: `${w}%` }}
                            />
                        ))}
                    </div>
                </div>
            )
        }

        if (stage === BulkUploadStage.STAGING && selectedFile) {
            return (
                <>
                    <div
                        className="
                            mt-[24px] flex items-center justify-between gap-[12px]
                            rounded-[8px] border border-solid border-brand-black-transparent-010
                            bg-brand-offWhite py-[16px] px-[24px]
                        "
                    >
                        <PrimaryText
                            typography="subtitleSemibold"
                            className="truncate flex-1 min-w-0"
                        >
                            {selectedFile.name}
                        </PrimaryText>

                        <button
                            type="button"
                            onClick={onRemoveFile}
                            className="flex-shrink-0 p-[8px] cursor-pointer hover:opacity-80"
                            aria-label="Remove file"
                        >
                            <img
                                src={trashIcon}
                                alt="Remove file"
                                className="w-[16px] h-[16px]"
                            />
                        </button>
                    </div>

                    <div className="flex justify-between gap-[12px] mt-[24px]">
                        <ButtonElement
                            label="Cancel"
                            type="default"
                            htmlType="button"
                            onClick={onRemoveFile}
                        />

                        <ButtonElement
                            label="Import rules"
                            type="primary"
                            htmlType="button"
                            onClick={runBulkUpload}
                            disabled={!selectedPracticeRole}
                        />
                    </div>
                </>
            )
        }

        // initial
        return (
            <>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept={NETWORK_STATUS_CONFIG.csvAccept}
                    className="hidden"
                    onChange={onFileInputChange}
                />

                <div
                    className="
                        flex flex-col items-center justify-center cursor-pointer
                        min-h-[160px] p-[24px] mt-[24px] rounded-[8px] gap-[8px]
                        border-2 border-dashed border-brand-warmShadow
                    "
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    onClick={onManualSelect}
                >
                    <PrimaryText typography="subtitleSemibold">
                        Add Network Rules CSV
                    </PrimaryText>

                    <PrimaryText
                        typography="subtitle"
                        color={NIRVANA_COLORS.brand.warmShadow}
                    >
                        Drop your CSV in here, or{" "}
                        <span className="text-brand-vibrantPurple underline cursor-pointer">
                            manually select
                        </span>
                    </PrimaryText>
                </div>
            </>
        )
    }

    return (
        <PopupWrapperElement
            isActive={props.isActive}
            className={props.className}
            onDone={props.onDone}
            closeOnOutsideClick={props.closeOnOutsideClick}
            withCloseIcon={stage !== BulkUploadStage.PARSING}
            popupWidthClassName="!w-[560px] !bg-brand-white"
        >
            <div className="pt-[8px]">
                <PrimaryText
                    typography="h5"
                    color={NIRVANA_COLORS.brand.black}
                    className="mb-[8px]"
                >
                    Add bulk new rules
                </PrimaryText>

                {stage !== BulkUploadStage.PARSING ? (
                    <>
                        <PrimaryText
                            typography="subtitle"
                            color={NIRVANA_COLORS.brand.warmShadow}
                        >
                            Import multiple network rules at once with a CSV.
                        </PrimaryText>

                        <button
                            type="button"
                            onClick={onDownloadTemplate}
                            className="
                                text-left p-0 border-0 bg-transparent cursor-pointer
                                hover:opacity-80
                            "
                        >
                            <PrimaryText typography="subtitle" underline>
                                Download template
                            </PrimaryText>
                        </button>
                    </>
                ) : null}

                {renderContent()}
            </div>
        </PopupWrapperElement>
    )
}

interface INetworkStatusRulesBulkUploadPopupComponentProps extends IPopupProps {
    onValidationDone: (data: IReviewNewRulesLocationState) => void
}
